/**
 * The console's live connection — one `EventSource` on `GET /api/live`.
 *
 * See docs/superpowers/plans/2026-08-26-webapp-live-updates.md (Task 5) and
 * `src/server/live/sse.ts`, which is the other end of this wire. The server
 * writes four named frames and one comment:
 *
 *   `ready`   — the stream is open, scope resolved (`{ userId, jobSiteCount }`)
 *   `record`  — a record the caller MAY see changed (`{ kind, id, jobSiteId }`)
 *   `scope`   — the caller's job sites changed under them (`{ jobSiteCount }`)
 *   `closed`  — the server is closing this stream (`{ reason }`)
 *   `:heartbeat …` — an SSE comment, which `EventSource` never surfaces
 *
 * This module turns those into exactly two things the UI needs: **refresh the
 * list**, and **what the connection is doing right now**.
 *
 * ## It refreshes; it does not patch
 *
 * A `record` frame carries an id and a kind, not a row. The console's rows
 * are a SERVER-side projection — grouping, stall verdicts, ball-in-court, the
 * sidebar's per-site open counts — so a client that tried to splice one
 * record into the rendered list would be reimplementing `listItems` in the
 * browser and would drift from it on the first change to either. Refetching
 * `/api/items` is one request that cannot disagree with the server.
 *
 * For the same reason there is no per-row filtering here. The server has
 * already refused every event outside this caller's scope (`sse.ts`'s one
 * rule), and an INSERT is by definition not a visible row yet — filtering
 * client-side on "is this row on screen" would drop exactly the events this
 * feature exists to deliver. Every in-scope event refreshes.
 *
 * ## Coalescing, and why it is a window and not a debounce-per-event
 *
 * A walk finishing produces a burst: a dozen records land in Mongo inside a
 * second. One refresh per event would put a dozen `/api/items` queries on the
 * server at the exact moment it is busiest. So the first event opens a
 * WINDOW; every event inside it is free; the window closes into a single
 * refresh. And if events keep arriving while that refresh is in flight, they
 * collapse into ONE follow-up, opened only after the in-flight one settles —
 * so the request rate is bounded by how fast the server can answer, never by
 * how fast records arrive.
 *
 * ## Reconnection is EventSource's own
 *
 * The browser retries on its own schedule (`sse.ts` sets it with `retry:`).
 * Nothing here re-opens a stream — a hand-rolled retry on top of the built-in
 * one is two loops racing, which is how a dropped connection becomes a
 * connection storm. The one thing this module does add is a refresh AFTER a
 * reconnect: while the stream was down, changes happened that nobody told us
 * about, so the rendered list is stale by an unknown amount.
 *
 * The exception is the server's own `closed` frame. That means "this caller
 * is no longer authenticated / no longer permitted", and a browser left to
 * itself would reconnect, earn a fresh 401, and repeat every few seconds
 * forever. So that one frame closes the source for good.
 *
 * ## Nothing here may break the page
 *
 * `sse.ts` made a live-path failure non-fatal on the server; this is the
 * client half of the same rule. There is no environment in which constructing
 * this makes the console worse than it was before Plan 4: no `EventSource`
 * global, a constructor that throws, a 503 the browser refuses to retry, a
 * refresh that rejects — each ends in a STATUS, never an exception. A console
 * with no live updates is a console you reload; that is the pre-Plan-4
 * console, and it is fully correct.
 */

// ---------------------------------------------------------------------------
// The transport, as narrowly as this module uses it
// ---------------------------------------------------------------------------

import { hasAuthAccessor, withAuthParam } from "../auth/token";
import { consoleUrl } from "../basePath";

/** The part of the DOM `EventSource` this module touches. Declaring it
 *  structurally is what lets `liveConnection.test.ts` drive the frames and
 *  both error readyStates without a browser. */
export interface EventSourceLike {
  readonly readyState: number;
  addEventListener(type: string, listener: (event: MessageEvent) => void): void;
  close(): void;
}

export type EventSourceConstructor = new (url: string) => EventSourceLike;

/** `EventSource.CLOSED` — the browser has GIVEN UP. It reports this after a
 *  non-200 response (our 503) or a wrong content type, neither of which it
 *  will retry. `CONNECTING` (0) is the retry-in-progress case. */
const READY_STATE_CLOSED = 2;

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

/**
 * What the console tells the user about its own freshness.
 *
 * `off` is deliberately not called "error": from the reader's point of view
 * the fact that matters is not that a request failed, it is that **this list
 * will not update itself** — and a stale list that looks live is worse than
 * one that admits it is stale.
 */
export type LiveStatus =
  /** Opened, no `ready` frame yet. */
  | "connecting"
  /** The server has confirmed the stream. Rows update themselves. */
  | "live"
  /** Dropped; the browser is retrying on its own. Rows may be stale. */
  | "reconnecting"
  /** No live path at all — and none is coming without a reload. */
  | "off";

// ---------------------------------------------------------------------------

export interface LiveConnectionOptions {
  /** Called when something in this caller's scope changed. Coalesced — see
   *  the file header. May return a promise; the next window does not open
   *  until it settles. */
  onRefresh: () => void | Promise<void>;
  /** Immediately discard snapshots on scope changes, deletion, or revoked access.
   * This is separate from the coalesced refresh: private data must not wait
   * for the window or an earlier request to settle before disappearing. */
  onDiscard?: () => void;
  /** Every status transition, in order. */
  onStatus?: (status: LiveStatus) => void;
  /** Defaults to `/api/live`. */
  url?: string;
  /** The coalescing window. Long enough to swallow a burst, short enough that
   *  a single change still feels immediate. */
  coalesceMs?: number;
  /**
   * How long before the stream is re-opened with a freshly minted token.
   * Must stay comfortably under Clerk's ~60s session-token lifetime — see the
   * renewal comment in the body. Injectable so a test can prove renewal
   * happens without waiting 45 real seconds; it is otherwise never passed.
   */
  renewMs?: number;
  /**
   * The EventSource constructor. `undefined` (the default) uses the
   * environment's; **`null` means the environment has none**, which is a real
   * state — an old browser, a locked-down webview — and is why the two are
   * distinguished rather than collapsed into a falsy check.
   */
  eventSource?: EventSourceConstructor | null;
}

export interface LiveConnection {
  status(): LiveStatus;
  /** Idempotent. Closes the source and cancels any pending refresh — after
   *  this, no `onRefresh` can fire. */
  close(): void;
}

// Mounted, like every other request the console makes. An SSE stream
// opened at the host root from a path-mounted console would reach the wrong
// origin document and never emit a `ready` frame.
const DEFAULT_URL = consoleUrl("/api/live");

/** 45s, against Clerk's ~60s token lifetime — early enough that renewal never
 *  races expiry, late enough that a console does not churn connections. */
const DEFAULT_RENEW_MS = 45_000;
const DEFAULT_COALESCE_MS = 250;

function environmentEventSource(): EventSourceConstructor | null {
  const candidate = (globalThis as { EventSource?: unknown }).EventSource;
  return typeof candidate === "function"
    ? (candidate as EventSourceConstructor)
    : null;
}

/**
 * Opens the live connection. NEVER throws: every failure is a `LiveStatus`.
 */
export function connectLive(options: LiveConnectionOptions): LiveConnection {
  const {
    onRefresh,
    onStatus,
    url = DEFAULT_URL,
    coalesceMs = DEFAULT_COALESCE_MS,
    renewMs = DEFAULT_RENEW_MS,
  } = options;
  const Ctor =
    options.eventSource === undefined ? environmentEventSource() : options.eventSource;

  let status: LiveStatus = "connecting";
  let closed = false;
  let source: EventSourceLike | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let refreshing = false;
  let queued = false;
  /** A `ready` after the first one is a RECONNECT — see the file header. */
  let everReady = false;

  function discard(): void {
    try { options.onDiscard?.(); }
    catch (err) { console.error("[live] a discard listener threw", err); }
  }

  function setStatus(next: LiveStatus): void {
    if (status === next) return;
    status = next;
    try {
      onStatus?.(next);
    } catch (err) {
      // A UI callback must not be able to take the connection down with it.
      console.error("[live] a status listener threw", err);
    }
  }

  async function runRefresh(): Promise<void> {
    timer = undefined;
    if (closed) return;
    refreshing = true;
    try {
      await onRefresh();
    } catch (err) {
      // A failed refresh is the page's problem to render, not this
      // connection's to die of. The next event tries again.
      console.error("[live] a refresh failed", err);
    } finally {
      refreshing = false;
      if (queued && !closed) {
        queued = false;
        schedule();
      }
    }
  }

  /** Opens the coalescing window, or joins the one already open. */
  function schedule(): void {
    if (closed) return;
    if (refreshing) {
      // Do not stack a second refresh onto an in-flight one; remember that
      // SOMETHING happened and open exactly one window when it settles.
      queued = true;
      return;
    }
    if (timer !== undefined) return;
    timer = setTimeout(() => void runRefresh(), coalesceMs);
  }

  function stop(next: LiveStatus): void {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
    queued = false;
    try {
      source?.close();
    } catch {
      // Already closed by the runtime. Nothing left to release.
    }
    source = undefined;
    setStatus(next);
  }

  if (!Ctor) {
    // No transport at all. The console still renders every row it fetched;
    // it simply will not update itself, and now says so.
    setStatus("off");
    return { status: () => status, close: () => {} };
  }

  /**
   * Re-open the stream with a freshly minted token, before the current one
   * expires.
   *
   * A Clerk session token lives ~60s, and `EventSource`'s own retry reconnects
   * to the URL IT WAS GIVEN — the one with the now-expired token in it. Left
   * alone, a signed-in console would go live for about a minute and then
   * reconnect into a 401 loop forever, reporting "reconnecting" while never
   * again delivering an event. That is the precise failure this plan's own
   * gate test exists to catch (a stream that looks alive and is not), so it is
   * not acceptable to leave to chance.
   *
   * Undefined when no token is in play — under the dev-auth bypass there is no
   * credential to expire, so no timer is armed and the connection behaves
   * exactly as it did before sign-in existed. The whole test suite runs in
   * that case.
   */
  let renewTimer: ReturnType<typeof setTimeout> | undefined;

  function clearRenew(): void {
    if (renewTimer !== undefined) {
      clearTimeout(renewTimer);
      renewTimer = undefined;
    }
  }

  function wire(src: EventSourceLike): void {
    src.addEventListener("ready", () => {
    if (closed) return;
    setStatus("live");
    if (everReady) {
      // A RECONNECT. Whatever changed while the stream was down was never
      // delivered, so the rendered list is stale by an unknown amount.
      schedule();
    }
    everReady = true;
  });

    src.addEventListener("record", (event) => {
    if (closed) return;
    try {
      if (JSON.parse(event.data).operation === "delete") {
        discard();
      }
    } catch { /* A malformed notification still causes an authoritative refresh. */ }
    // Every event that reaches this client is already in scope — `sse.ts`
    // refused the rest. See the file header on why nothing is filtered here.
    schedule();
  });

    src.addEventListener("scope", () => {
    if (closed) return;
    discard();
    // The caller's job sites changed under them: rows they may now see, or
    // may no longer see. Only a refetch knows which.
    schedule();
  });

    src.addEventListener("closed", () => {
    if (closed) return;
    discard();
    // The server is done with this stream (revoked, signed out). Left alone,
    // the browser would reconnect into a 401 loop.
    closed = true;
    stop("off");
  });

    src.addEventListener("error", () => {
    if (closed) return;
    // The browser owns the retry. All this decides is what to TELL the user:
    // CLOSED means it has given up (our 503, a bad content type), anything
    // else means it is still trying.
    setStatus(source?.readyState === READY_STATE_CLOSED ? "off" : "reconnecting");
    });
  }

  /**
   * Build the URL (with a token when there is one), open the stream, wire it.
   *
   * Async because minting the token is. The public function stays synchronous
   * and returns immediately in "connecting", which is the state it reported
   * during the browser's own connect anyway — so no caller can tell the
   * difference, and none has to await a token to render.
   */
  function start(target: string): void {
    if (closed) return;
    try {
      source = new Ctor!(target);
    } catch (err) {
      console.error(`[live] could not open ${url}; the console will not update itself`, err);
      setStatus("off");
      return;
    }
    wire(source);

    // Only arm renewal when a credential is actually in the URL. `target !==
    // url` is the exact test for that, and it keeps the bypass path timer-free.
    if (target !== url) {
      clearRenew();
      renewTimer = setTimeout(() => {
        if (closed) return;
        try {
          source?.close();
        } catch {
          // Already closed by the runtime.
        }
        source = undefined;
        void open();
      }, renewMs);
    }
  }

  /**
   * Open the stream, synchronously when there is no token to mint.
   *
   * The synchronous path is not an optimisation — it is the ORIGINAL
   * behaviour, and every caller that opens a connection and immediately
   * dispatches an event depends on it. Going async unconditionally would
   * change the semantics of the unauthenticated path (the dev-auth bypass,
   * and the entire test suite) to accommodate a token it never fetches.
   */
  function open(): void {
    if (closed) return;

    if (!hasAuthAccessor()) {
      start(url);
      return;
    }

    void (async () => {
      let target = url;
      try {
        target = await withAuthParam(url);
      } catch {
        // No token to be had. Open unauthenticated and let the server answer;
        // a 401 surfaces as "off", which is honest, rather than never opening.
      }
      start(target);
    })();
  }

  open();

  return {
    status: () => status,
    close: () => {
      if (closed) return;
      closed = true;
      clearRenew();
      stop("off");
    },
  };
}
